import React, { useEffect, useRef } from 'react';
import { FocusOn } from 'react-focus-on';
import classnames from 'classnames';
import { useOutsideClick } from '../../hooks/use-outside-click';
import { Text } from '../text';
import { Heading, HeadingProps } from '../heading';
import { TextButton, TextButtonProps } from '../text-button';
import { TextLink, TextLinkProps } from '../link';

import styles from './dialog.module.scss';
import { isTextLinkPropsWithoutVariant } from '../../utils/is-link-props';

type DialogTextButtonProps = Omit<TextButtonProps, 'variant'>;
type DialogTextLinkProps = Omit<TextLinkProps, 'variant'>;
export type DialogCTAType = DialogTextButtonProps | DialogTextLinkProps;

export type DialogProps = {
  isOpen: boolean;
  outsideClick: () => void;
  ariaDescription: string;
  dialogLabelledBy?: string;
  text: string;
  headline: Omit<HeadingProps, 'variant'>;
  primaryCTA: DialogCTAType;
  secondaryCTA?: DialogCTAType;
  tertiaryCTA?: DialogCTAType;
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

const baseClass = 'dialog';
const noScrollClass = 'no-scroll';

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  headline,
  text,
  outsideClick,
  ariaDescription,
  dialogLabelledBy,
  primaryCTA,
  secondaryCTA,
  tertiaryCTA,
  className
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const classes = classnames(styles.dialog, { [styles[`${baseClass}--open`]]: isOpen }, className);
  const ctaContainerVertical = classnames(
    styles[`${baseClass}__cta-container`],
    styles[`${baseClass}__cta-container--vertical`]
  );
  const ctaContainerHorizontal = classnames(
    styles[`${baseClass}__cta-container`],
    styles[`${baseClass}__cta-container--horizontal`]
  );

  const ctaClasses = classnames(styles[`${baseClass}__cta`]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(noScrollClass);

      if (!ref.current || !scrollContainerRef.current) {
        return;
      }

      scrollContainerRef.current.scrollTop = 0;
    }

    return () => {
      document.body.classList.remove(noScrollClass);
    };
  }, [isOpen]);

  useOutsideClick(ref, outsideClick);

  return (
    <>
      <FocusOn enabled={isOpen} onEscapeKey={outsideClick} autoFocus={false}>
        <div
          aria-hidden={!isOpen}
          className={classes}
          aria-modal={isOpen}
          aria-describedby={`${baseClass}-description`}
          data-testid="dialog-component"
          aria-labelledby={dialogLabelledBy || ''}
        >
          <div className={styles[`${baseClass}__inner`]} ref={ref}>
            <div className={styles[`${baseClass}__inner__scroll-container`]} ref={scrollContainerRef}>
              <div>
                <Heading {...headline} variant="headline-functional-400" className={styles[`${baseClass}__headline`]}>
                  {headline.children}
                </Heading>
                <Text className={styles[`${baseClass}__text`]}>{text}</Text>
              </div>

              {/* On small screens buttons will be displayed stacked and in order tertiary-secondary-primary */}
              <div className={ctaContainerVertical}>
                {isTextLinkPropsWithoutVariant(primaryCTA) ? (
                  <TextLink {...primaryCTA} variant="button-primary" className={ctaClasses} />
                ) : (
                  <TextButton {...primaryCTA} variant="primary" className={ctaClasses} />
                )}
                {secondaryCTA &&
                  (isTextLinkPropsWithoutVariant(secondaryCTA) ? (
                    <TextLink {...secondaryCTA} variant="button-secondary" className={ctaClasses} />
                  ) : (
                    <TextButton {...secondaryCTA} variant="secondary" className={ctaClasses} />
                  ))}
                {tertiaryCTA &&
                  (isTextLinkPropsWithoutVariant(tertiaryCTA) ? (
                    <TextLink {...tertiaryCTA} variant="button-ghost" className={ctaClasses} />
                  ) : (
                    <TextButton {...tertiaryCTA} variant="ghost" className={ctaClasses} />
                  ))}
              </div>

              {/* Buttons will be displayed in inverted order on larger screens */}
              <div className={ctaContainerHorizontal}>
                {tertiaryCTA &&
                  (isTextLinkPropsWithoutVariant(tertiaryCTA) ? (
                    <TextLink {...tertiaryCTA} variant="button-ghost" className={ctaClasses} />
                  ) : (
                    <TextButton {...tertiaryCTA} variant="ghost" className={ctaClasses} />
                  ))}
                {secondaryCTA &&
                  (isTextLinkPropsWithoutVariant(secondaryCTA) ? (
                    <TextLink {...secondaryCTA} variant="button-secondary" className={ctaClasses} />
                  ) : (
                    <TextButton {...secondaryCTA} variant="secondary" className={ctaClasses} />
                  ))}
                {isTextLinkPropsWithoutVariant(primaryCTA) ? (
                  <TextLink {...primaryCTA} variant="button-primary" className={ctaClasses} />
                ) : (
                  <TextButton {...primaryCTA} variant="primary" className={ctaClasses} />
                )}
              </div>
            </div>
            <div className={styles[`${baseClass}__hidden-description`]} id={`${baseClass}-description`}>
              {ariaDescription}
            </div>
          </div>
        </div>
      </FocusOn>
      {isOpen && <div className={styles[`${baseClass}-layer`]} />}
    </>
  );
};
